import { useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

import { useGraphQLClient } from "@package/graphql/react";
import { logger } from "@artis/logger";

import { type JWT, orgIdToEnv } from "../../globals";
import { MfaStateQuery, SetMfaOptOutMutation } from "./queries";
import { asMfaMode, canDisableMfa, deriveMfaUserState, mfaSettingsView, shouldOfferMfa } from "./state";
import { useClaims, useUserId } from "../../context/auth/hooks";

const MFA_STATE_KEY = "mfa-state";
const MFA_ENROLLMENT_KEY = "mfa-enrollment";

const toError = (error: unknown) => (error instanceof Error ? error : new Error(String(error)));

const envFromClaims = (orgId: JWT["org_id"] | undefined) => {
  if (!orgId) return undefined;
  try {
    return orgIdToEnv(orgId);
  } catch {
    return undefined;
  }
};

const fetchMfaEnrolled = async (token: string, env: string): Promise<boolean> => {
  const res = await fetch("/.netlify/functions/mfa-status", {
    method: "POST",
    headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
    body: JSON.stringify({ env }),
  });
  if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
  const data = (await res.json()) as { enrolled?: boolean };
  return Boolean(data.enrolled);
};

export const useMfaState = () => {
  const graphql = useGraphQLClient();
  const userId = useUserId();
  const claims = useClaims();
  const { getAccessTokenSilently } = useAuth0();
  const env = envFromClaims(claims.data?.org_id);

  const orgId = Number(claims.data?.["https://hasura.io/jwt/claims"]?.["x-hasura-org-id"]);
  const enabled = Boolean(userId) && Number.isFinite(orgId);

  const configQuery = useQuery({
    enabled,
    queryKey: [MFA_STATE_KEY, userId, orgId],
    queryFn: () => graphql.execute(MfaStateQuery, { userId: userId ?? "", orgId }).then((r) => r.data),
  });

  const mode = asMfaMode(configQuery.data?.organisation?.[0]?.mfa_mode);
  const optedOut = Boolean(configQuery.data?.folio_user?.[0]?.mfa_opted_out);

  const needsEnrollment = enabled && mode !== "off" && Boolean(env);
  const enrollmentQuery = useQuery({
    enabled: needsEnrollment,
    queryKey: [MFA_ENROLLMENT_KEY, userId, env],
    queryFn: async () => {
      if (!env) throw new Error("Cannot resolve environment from token");
      return fetchMfaEnrolled(await getAccessTokenSilently(), env);
    },
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const enrolled = Boolean(enrollmentQuery.data);
  const state = deriveMfaUserState(enrolled, optedOut);

  return {
    isLoading: configQuery.isLoading || (needsEnrollment && enrollmentQuery.isLoading),
    mode,
    state,
    offer: shouldOfferMfa(mode, state),
    settingsView: mfaSettingsView(mode, state),
    canDisable: canDisableMfa(mode, state),
  };
};

/** Re-login with the enrolment trigger so the Auth0 action enrols the user. */
export const useEnableMfa = () => {
  const { loginWithRedirect } = useAuth0();
  return useCallback(
    () => loginWithRedirect({ authorizationParams: { "ext-enroll_mfa": "true" } }),
    [loginWithRedirect],
  );
};

/** Persist a permanent (v1) opt-out for the current user. */
export const useOptOutOfMfa = () => {
  const graphql = useGraphQLClient();
  const userId = useUserId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => graphql.execute(SetMfaOptOutMutation, { userId: userId ?? "" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [MFA_STATE_KEY] }),
    onError: (error: unknown) => {
      logger.error("Failed to opt out of MFA", toError(error));
      toast.error("Couldn't save your choice. Please try again.");
    },
  });
};

/** Remove the current user's TOTP factor via the hardened backend endpoint. */
export const useDisableMfa = () => {
  const { getAccessTokenSilently } = useAuth0();
  const claims = useClaims();
  const env = envFromClaims(claims.data?.org_id);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      if (!env) throw new Error("Cannot resolve environment from token");
      const token = await getAccessTokenSilently();
      const response = await fetch("/.netlify/functions/disable-mfa", {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
        body: JSON.stringify({ env }),
      });
      if (!response.ok) throw new Error((await response.text()) || `HTTP ${response.status}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [MFA_STATE_KEY] });
      queryClient.invalidateQueries({ queryKey: [MFA_ENROLLMENT_KEY] });
      toast.success("Two-factor authentication disabled.");
    },
    onError: (error: unknown) => {
      logger.error("Failed to disable MFA", toError(error));
      toast.error("Couldn't disable two-factor authentication.");
    },
  });
};
